import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { UncontrolledTooltip } from 'reactstrap';
import styled from 'styled-components';
import { FlexDiv, VerticalDiv } from '../../../../../components/Common';
import Dropdown from '../../../../../components/Dropdown';
import MultiSelectWithTags from '../../../../../components/MultiSelectWithTags';
import {
    BOLTING,
    DEVELOPMENT,
    DISEASES_DESCRIPTION,
    DISEASES_GRAVITY,
    DISEASES_GRAVITY_VALUES,
    REMARKS,
    ReportCharactersNames,
    ROGUING_REMARKS,
    STANDING,
    UNIFORMITY,
    UNIFORMITY_VALUES
} from '../../../../../res/Constants';
import {
    getReportCharacterNumericErrorText,
    isReportCharacterNumericValid
} from '../../../../../service/CommonFunctions';
import dimension from './../../../../../res/styles/Responsive';

const DEVELOPMENT_VALUES = [1, 2, 3, 4];

// Parse diseases string - could be a string separated by |
const parseInitialDiseases = (value) => {
    if (!value || typeof value !== 'string') return [];
    return value.split('|').map(d => d.trim()).filter(d => d);
};

EditableLine.propTypes = {
    lineName: PropTypes.string,
    initialData: PropTypes.object,
    onValuesEdited: PropTypes.func,
    diseasesOptions: PropTypes.array,
    isNewReport: PropTypes.bool
};

EditableLine.defaultProps = {
    diseasesOptions: []
};

export default function EditableLine({ lineName, initialData, onValuesEdited, diseasesOptions, isNewReport }) {
    const {
        Stand,
        Develop,
        Uniformity,
        Bolt,
        DiseasesDescription,
        DiseasesGravity,
        RoguingRemarks,
        Remarks
    } = initialData;

    // Local state for all fields to maintain control
    const [stand, setStand] = useState(Stand || '0');
    const [develop, setDevelop] = useState(Develop);
    const [uniformity, setUniformity] = useState(Uniformity);
    const [bolt, setBolt] = useState(Bolt || '0');
    const [diseasesGravity, setDiseasesGravity] = useState(DiseasesGravity);
    const [roguingRemarks, setRoguingRemarks] = useState(RoguingRemarks);
    const [remarks, setRemarks] = useState(Remarks);
    const [selectedDiseases, setSelectedDiseases] = useState(() =>
        parseInitialDiseases(DiseasesDescription)
    );

    // Update local state when initialData changes (for switching between reports)
    useEffect(() => {
        setStand(Stand || '0');
        setDevelop(Develop);
        setUniformity(Uniformity);
        setBolt(Bolt || '0');
        setDiseasesGravity(DiseasesGravity);
        setRoguingRemarks(RoguingRemarks);
        setRemarks(Remarks);
        setSelectedDiseases(parseInitialDiseases(DiseasesDescription));
    }, [Stand, Develop, Uniformity, Bolt, DiseasesGravity, RoguingRemarks, Remarks, DiseasesDescription]);

    const targetBolt = `${ReportCharactersNames.bolt}${lineName}`;
    const targetStand = `${ReportCharactersNames.stand}${lineName}`;

    // Handlers that update both local state and parent
    const handleStandChange = (e) => {
        const value = e.target.value;
        setStand(value);
        onValuesEdited(ReportCharactersNames.stand, value === '' ? '0' : value);
    };

    const handleStandFocus = (e) => {
        if (e.target.value === '0') {
            e.target.value = '';
            setStand('');
        }
    };

    const handleStandBlur = (e) => {
        if (e.target.value === '' || e.target.value === '0') {
            setStand('0');
            onValuesEdited(ReportCharactersNames.stand, '0');
        }
    };

    const handleDevelopChange = (v) => {
        setDevelop(v);
        onValuesEdited(ReportCharactersNames.develop, v);
    };

    const handleUniformityChange = (v) => {
        setUniformity(v);
        onValuesEdited(ReportCharactersNames.uniform, v);
    };

    const handleBoltChange = (e) => {
        const value = e.target.value;
        setBolt(value);
        onValuesEdited(ReportCharactersNames.bolt, value === '' ? '0' : value);
    };

    const handleBoltFocus = (e) => {
        if (e.target.value === '0') {
            e.target.value = '';
            setBolt('');
        }
    };

    const handleBoltBlur = (e) => {
        if (e.target.value === '' || e.target.value === '0') {
            setBolt('0');
            onValuesEdited(ReportCharactersNames.bolt, '0');
        }
    };

    const handleDiseasesGravityChange = (v) => {
        setDiseasesGravity(v);
        onValuesEdited(ReportCharactersNames.diseasesGravity, v);
    };

    const handleRoguingRemarksChange = (e) => {
        const value = e.target.value;
        setRoguingRemarks(value);
        onValuesEdited(ReportCharactersNames.roguing, value);
    };

    const handleRemarksChange = (e) => {
        const value = e.target.value;
        setRemarks(value);
        onValuesEdited(ReportCharactersNames.remarks, value);
    };

    const handleDiseasesChange = (newDiseases) => {
        setSelectedDiseases(newDiseases);
        const diseasesString = newDiseases.join('|');
        onValuesEdited(ReportCharactersNames.diseasesDescription, diseasesString);
    };

    return (
        <VerticalDiv>
            <TitleContainer>
                <h5>Line {lineName}</h5>
            </TitleContainer>
            <FlexDiv orientation='row' justifyContent='space-between'>
                <InputTitle>{STANDING}</InputTitle>
                <Input
                    type='number'
                    value={stand}
                    id={targetStand}
                    onChange={handleStandChange}
                    onFocus={handleStandFocus}
                    onBlur={handleStandBlur}
                />
                {!isReportCharacterNumericValid(stand) && (
                    <UncontrolledTooltip placement='bottom' target={targetStand}>
                        {getReportCharacterNumericErrorText(stand)}
                    </UncontrolledTooltip>
                )}
            </FlexDiv>
            <FlexDiv orientation='row' justifyContent='space-between'>
                <InputTitle>{DEVELOPMENT}</InputTitle>
                <StyledDropdown
                    emptyOption={false}
                    options={DEVELOPMENT_VALUES}
                    value={develop}
                    onChange={handleDevelopChange}
                />
            </FlexDiv>
            <FlexDiv orientation='row' justifyContent='space-between'>
                <InputTitle>{UNIFORMITY}</InputTitle>
                <StyledDropdown
                    emptyOption={false}
                    options={Object.keys(UNIFORMITY_VALUES)}
                    value={uniformity}
                    onChange={handleUniformityChange}
                />
            </FlexDiv>
            <FlexDiv orientation='row' justifyContent='space-between'>
                <InputTitle>{BOLTING}</InputTitle>
                <Input
                    type='text'
                    value={bolt}
                    id={targetBolt}
                    onChange={handleBoltChange}
                    onFocus={handleBoltFocus}
                    onBlur={handleBoltBlur}
                />
                {!isReportCharacterNumericValid(bolt) && (
                    <UncontrolledTooltip placement='bottom' target={targetBolt}>
                        {getReportCharacterNumericErrorText(bolt)}
                    </UncontrolledTooltip>
                )}
            </FlexDiv>
            <FlexDiv orientation='row' justifyContent='space-between'>
                <InputTitle>{DISEASES_GRAVITY}</InputTitle>
                <StyledDropdown
                    emptyOption={false}
                    options={DISEASES_GRAVITY_VALUES}
                    value={diseasesGravity}
                    onChange={handleDiseasesGravityChange}
                />
            </FlexDiv>
            <FlexDiv orientation='row' justifyContent='space-between'>
                <InputTitle>{DISEASES_DESCRIPTION}</InputTitle>
                <MultiSelectWithTags
                    options={diseasesOptions}
                    selectedValues={selectedDiseases}
                    onChange={handleDiseasesChange}
                    placeholder="Select or digit a desease"
                />
            </FlexDiv>
            <Label htmlFor={ReportCharactersNames.roguing}>
                {ROGUING_REMARKS}
                <textarea
                    name={ReportCharactersNames.roguing}
                    id={ReportCharactersNames.roguing}
                    cols='10'
                    rows='2'
                    value={roguingRemarks}
                    onChange={handleRoguingRemarksChange}
                />
            </Label>
            <Label htmlFor={ReportCharactersNames.remarks}>
                {REMARKS}
                <textarea
                    name={ReportCharactersNames.remarks}
                    id={ReportCharactersNames.remarks}
                    cols='10'
                    rows='2'
                    value={remarks}
                    onChange={handleRemarksChange}
                />
            </Label>
        </VerticalDiv>
    );
}

const TitleContainer = styled.div`
    text-align: center;
`;

const Label = styled.label`
    width: 100%;
    display: flex;
    flex-direction: column;
`;

const StyledDropdown = styled(Dropdown)`
    width: 200px;
    text-indent: 10px;
`;

const Input = styled.input`
    height: 30px;
    width: 200px;
    border-radius: 5px;
    box-shadow: 0 1px 2px grey;
    margin-bottom: 10px;
    text-indent: 10px;

    :focus {
        outline: none;
    }
`;

const InputTitle = styled.p`
    width: min-content;

    @media ${dimension.md} {
        width: auto;
    }
`;